import React, {useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useBbox, useComponentProps, useEffectEvent} from 'helpers/hooks/utils';
import utils from 'helpers/utils';
import Box from 'components/atoms/Layout/Box/Box';
import Icon from 'components/atoms/Icons/Icon/Icon';
import Typography, {H6} from 'components/atoms/Text/Typography/Typography';
import constants from 'helpers/constants';
import Img from 'components/atoms/Images/Img/Img';
import StyledActionCard from 'components/organisms/Cards/ActionCard/ActionCard.styles';
import {Database, Dot} from 'assets/icons';
import Chip from 'components/atoms/Chips/Chip/Chip';
import ChipList from 'components/atoms/Chips/ChipList/ChipList';
import Link from 'components/atoms/Links/Link/Link';
import Markdown from 'components/atoms/Formatters/Markdown/Markdown';
import InlineForm from 'components/organisms/Forms/InlineForm/InlineForm';

const ActionCard = React.forwardRef((props, ref) => {
  const {
    action,
    answers,
    pageIdx,
    pageCount,
    isLoading,
    onChange,
    onSubmit,
    clampDescription,
    ...innerProps
  } = useComponentProps({
    clampDescription: false,
    ...props
  }, 'ActionCard', {
    variable: ['type'],
    static: ['clampText']
  });

  const innerRef = useRef(null);
  const formRef = useRef(null);

  const [clampText, setClampText] = useState(clampDescription);
  const [clampActive, setClampActive] = useState(false);

  const actionCard = useMemo(() => ({
    refs: {
      ref: innerRef,
      formRef
    }
  }), []);

  useImperativeHandle(ref, () => actionCard);

  const actionType = constants.data.lookup('assistantAdviceActions', action?.id);
  const contentBox = useBbox(() => innerRef.current?.querySelector('.ActionCard-description'), ['clamped']);

  const fields = useMemo(() => {
    const fields = [];

    if ([constants.assistant.adviceActionTypes.askQuestion, constants.assistant.adviceActionTypes.needMoreInfo].includes(action?.id)) {
      action?.details?.forEach((question, idx) => {
        let answer = answers?.[idx] ?? null;
        let options = question?.options?.map((opt, idx) => ({
          label: opt?.label ?? opt,
          value: idx
        }));

        if (options?.length > 0) {
          fields.push({
            name: `answer_${idx}`,
            label: question.text,
            inlineLabel: 'Answer',
            type: constants.formFieldTypes.list,
            validation: question?.multiselect ? constants.formFieldValidationTypes.list : constants.formFieldValidationTypes.text,
            options: question?.options?.map((opt, idx) => ({
              label: opt?.label ?? opt,
              value: idx
            })),
            initial: answer,
            FormFieldProps: {
              variant: 'staticLabel',
              deselect: true,
              size: 'smaller',
              hidden: idx !== pageIdx,
              multiple: question?.multiselect,
              freeTextOption: question?.free ?? true,
              FreeTextOptionProps: {
                placeholder: 'Type your own...'
              }
            }
          });
        } else {
          fields.push({
            name: `answer_${idx}`,
            label: question.text,
            inlineLabel: 'Answer',
            placeholder: 'Type here...',
            type: constants.formFieldTypes.textarea,
            validation: constants.formFieldValidationTypes.text,
            initial: answer,
            FormFieldProps: {
              variant: 'staticLabel',
              hidden: idx !== pageIdx,
              size: 'smaller',
              minRows: 2
            }
          });
        }
      });
    }

    return fields;
  }, [action?.id, action?.details, answers, pageIdx]);

  useEffect(() => {
    if (clampText) {
      setClampActive(contentBox?.clamped);
    }
  }, [contentBox?.clamped, clampText]);

  const renderPages = () => {
    if (pageCount > 1) {
      return <Typography className="ActionCard-pages"
                         variant="caption"
                         color="text.secondary">
        {pageIdx + 1}/{pageCount}
      </Typography>
    }
  }

  const renderLogo = () => {
    if (actionType?.icon) {
      return <Box className="ActionCard-logo">
        {utils.isReactElement(actionType.icon) ? actionType.icon : <Icon icon={actionType.icon} isLoading={isLoading} color={actionType?.iconColor ?? 'primary'}/>}
        {renderPages()}
      </Box>
    } else if (actionType?.img) {
      return <Box className="ActionCard-logo">
        {utils.isReactElement(actionType.img) ? actionType.img : <Img src={actionType.img} alt="logo" isLoading={isLoading}/>}
        {renderPages()}
      </Box>
    } else if (isLoading) {
      return <Box className="ActionCard-logo">
        <Icon icon={Database} isLoading={isLoading}/>
        {renderPages()}
      </Box>
    }
  }

  const onChangeEvent = useEffectEvent(onChange);
  const handleChangeDirect = useCallback((e) => {
    if (e?.target?.name === `answer_${pageIdx}`) {
      onChangeEvent?.(e?.target?.value);
    }
  }, [pageIdx, onChangeEvent]);

  const onSubmitEvent = useEffectEvent(onSubmit);
  const handleSubmit = useCallback((values) => {
    onSubmitEvent?.(values[`answer_${pageIdx}`]);
  }, [pageIdx, onSubmitEvent]);

  const renderDetails = () => {
    if ([constants.assistant.adviceActionTypes.askQuestion, constants.assistant.adviceActionTypes.needMoreInfo].includes(action?.id)) {
      return <InlineForm ref={formRef}
                         fields={fields}
                         autoFocus={false}
                         onChangeDirect={handleChangeDirect}
                         onSubmit={handleSubmit} />
    } else if (action?.id === constants.assistant.adviceActionTypes.addScope) {
      const projectTypeDef = constants.data.lookup('collectionProjectTypes', action?.details?.projectType);

      return <Box className="ActionCard-details">
        {projectTypeDef ? <Box className="ActionCard-detail">
          <Typography className="ActionCard-detail-label"
                      isLoading={isLoading}
                      variant="body2">Goal</Typography>
          <Typography className="ActionCard-detail-value"
                      isLoading={isLoading}
                      variant="body2">{projectTypeDef?.label}</Typography>
        </Box> : null}
        {action?.details?.description ? <Box className="ActionCard-detail">
          <Typography className="ActionCard-detail-label"
                      isLoading={isLoading}
                      variant="body2">Description</Typography>
          <Typography className="ActionCard-detail-value"
                      isLoading={isLoading}
                      variant="body2">{action?.details?.description}</Typography>
        </Box> : null}
        {action?.details?.rationale ? <Box className="ActionCard-detail">
          <Typography className="ActionCard-detail-label"
                      isLoading={isLoading}
                      variant="body2">Rationale</Typography>
          <Typography className="ActionCard-detail-value"
                      isLoading={isLoading}
                      variant="body2">{action?.details?.rationale}</Typography>
        </Box> : null}
        {action?.details?.projectTopics?.length > 0 ? <Box className="ActionCard-detail">
          <Typography className="ActionCard-detail-label"
                      isLoading={isLoading}
                      variant="body2">Topics</Typography>
          <ChipList className="ActionCard-detail-value">
            {action?.details?.projectTopics?.map((kw, idx) => {
              return <Chip key={idx}
                           size="small"
                           label={kw}
                           variant="transparent"
                           isLoading={isLoading}
                           color="primary"/>
            })}
          </ChipList>
        </Box> : null}
      </Box>
    } else if (action?.id === constants.assistant.adviceActionTypes.addSource) {
      const typeDef = constants.data.lookup('sourceTypes', action?.details?.type);
      const name = action?.details?.type !== constants.sources.types.suggestions ? action?.details?.name : null;
      const comment = action?.details?.type === constants.sources.types.suggestions ? typeDef.description : action?.details?.comment;

      return <Box className="ActionCard-details">
        <Box className="ActionCard-detail">
          <Typography className="ActionCard-detail-label"
                      isLoading={isLoading}
                      variant="body2">Type</Typography>
          <Typography className="ActionCard-detail-value"
                      isLoading={isLoading}
                      variant="body2">{typeDef?.label}</Typography>
        </Box>
        {name ? <Box className="ActionCard-detail">
          <Typography className="ActionCard-detail-label"
                      isLoading={isLoading}
                      variant="body2">Name</Typography>
          <Typography className="ActionCard-detail-value"
                      isLoading={isLoading}
                      variant="body2">{name}</Typography>
        </Box> : null}
        {comment ? <Box className="ActionCard-detail">
          <Typography className="ActionCard-detail-label"
                      isLoading={isLoading}
                      variant="body2">Description</Typography>
          <Typography className="ActionCard-detail-value"
                      isLoading={isLoading}
                      variant="body2">{comment}</Typography>
        </Box> : null}
        {[constants.sources.types.google, constants.sources.types.places, constants.sources.types.cordis].includes(typeDef?.value) ?
          <Box className="ActionCard-detail">
            <Typography className="ActionCard-detail-label"
                        isLoading={isLoading}
                        variant="body2">Query</Typography>
            <Typography className="ActionCard-detail-value"
                        isLoading={isLoading}
                        variant="body2">{action?.details?.query}</Typography>
          </Box> : (
            typeDef?.value === constants.sources.types.ai ? <Box className="ActionCard-detail">
              <Typography className="ActionCard-detail-label"
                          isLoading={isLoading}
                          variant="body2">Prompt</Typography>
              <Typography className="ActionCard-detail-value"
                          isLoading={isLoading}
                          variant="body2">{action?.details?.query}</Typography>
            </Box> : (
              typeDef?.value === constants.sources.types.website ?
                <Box className="ActionCard-detail">
                  <Typography className="ActionCard-detail-label"
                              isLoading={isLoading}
                              variant="body2">Link</Typography>
                  <Typography className="ActionCard-detail-value"
                              isLoading={isLoading}
                              variant="body2">
                    <Link showTooltip={true}
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => e.stopPropagation()}
                          href={utils.cleanExternalLink(action?.details?.link)}
                          target="_blank">{utils.displayExternalLink(action?.details?.link)}</Link>
                  </Typography>
                </Box> : (
                  typeDef?.value === constants.sources.types.database ? (
                    action?.details?.query ? <Box className="ActionCard-detail">
                        <Typography className="ActionCard-detail-label"
                                    isLoading={isLoading}
                                    variant="body2">Query</Typography>
                        <Typography className="ActionCard-detail-value"
                                    isLoading={isLoading}
                                    variant="body2">{action?.details?.query}</Typography>
                      </Box> : <Box className="ActionCard-detail">
                        <Typography className="ActionCard-detail-label"
                                    isLoading={isLoading}
                                    variant="body2">Keywords</Typography>
                        <ChipList className="ActionCard-detail-value">
                          {action?.details?.keywords?.map((kw, idx) => {
                            return <Chip key={idx}
                                         size="small"
                                         label={kw}
                                         variant="transparent"
                                         isLoading={isLoading}
                                         color="primary"/>
                          })}
                        </ChipList>
                      </Box>
                  ) : (
                    typeDef?.value === constants.sources.types.webFeed ?
                      <Box className="ActionCard-detail">
                        <Typography className="ActionCard-detail-label"
                                    isLoading={isLoading}
                                    variant="body2">{typeDef?.subTypes?.find((st) => st.value === action?.details?.subType)?.label}</Typography>
                        <Typography className="ActionCard-detail-value"
                                    isLoading={isLoading}
                                    variant="body2">
                          {action?.details?.link ?
                            <Link showTooltip={true}
                                  onMouseDown={(e) => e.stopPropagation()}
                                  onClick={(e) => e.stopPropagation()}
                                  href={utils.cleanExternalLink(action?.details?.link)}
                                  target="_blank">{utils.displayExternalLink(action?.details?.link)}</Link> :
                            action?.details?.query}
                        </Typography>
                      </Box> : null
                  )
                )
            )
          )}
      </Box>
    } else if (action?.id === constants.assistant.adviceActionTypes.addField) {
      const typeDef = constants.data.lookup('customFieldRendererTypes', action?.details?.renderer);
      return <Box className="ActionCard-details">
        <Box className="ActionCard-detail">
          <Typography className="ActionCard-detail-label"
                      isLoading={isLoading}
                      variant="body2">Type</Typography>
          <Typography className="ActionCard-detail-value"
                      isLoading={isLoading}
                      variant="body2">{typeDef?.label}</Typography>
        </Box>
        <Box className="ActionCard-detail">
          <Typography className="ActionCard-detail-label"
                      isLoading={isLoading}
                      variant="body2">Name</Typography>
          <Typography className="ActionCard-detail-value"
                      isLoading={isLoading}
                      variant="body2">{action?.details?.label}</Typography>
        </Box>
        <Box className="ActionCard-detail">
          <Typography className="ActionCard-detail-label"
                      isLoading={isLoading}
                      variant="body2">Group</Typography>
          <Typography className="ActionCard-detail-value"
                      isLoading={isLoading}
                      variant="body2">{action?.details?.groupName}</Typography>
        </Box>
        <Box className="ActionCard-detail">
          <Typography className="ActionCard-detail-label"
                      isLoading={isLoading}
                      variant="body2">Auto lookup</Typography>
          <Typography className="ActionCard-detail-value"
                      isLoading={isLoading}
                      variant="body2">{action?.details?.autoLookup ? 'Online' : 'Files'}</Typography>
        </Box>
        {action?.details?.tooltip ? <Box className="ActionCard-detail">
          <Typography className="ActionCard-detail-label"
                      isLoading={isLoading}
                      variant="body2">Description</Typography>
          <Typography className="ActionCard-detail-value"
                      isLoading={isLoading}
                      variant="body2">{action?.details?.tooltip}</Typography>
        </Box> : null}
      </Box>
    } else if ([
        constants.assistant.adviceActionTypes.addTagGroup,
        constants.assistant.adviceActionTypes.addTagGroupPoints,
        constants.assistant.adviceActionTypes.addTagGroupDescriptions,
        constants.assistant.adviceActionTypes.addTagGroupKeywords
      ].includes(action?.id)) {
      const autoSelect = action?.details?.autoTagType === constants.collection.autoTagTypes.byKeywords ? 'By keywords' : (
        action?.details?.autoTagType === constants.collection.autoTagTypes.byAI ? 'By AI' : (
          action?.details?.autoTagType === constants.collection.autoTagTypes.byAIOnline ? 'By AI (Online)' : 'Not active'
        )
      );

      return <Box className="ActionCard-details">
        <Box className="ActionCard-detail">
          <Typography className="ActionCard-detail-label"
                      isLoading={isLoading}
                      variant="body2">Name</Typography>
          <Typography className="ActionCard-detail-value"
                      isLoading={isLoading}
                      variant="body2">{action?.details?.name}</Typography>
        </Box>
        {action?.details?.description ? <Box className="ActionCard-detail">
          <Typography className="ActionCard-detail-label"
                      isLoading={isLoading}
                      variant="body2">Description</Typography>
          <Typography className="ActionCard-detail-value"
                      isLoading={isLoading}
                      variant="body2">{action?.details?.description}</Typography>
        </Box> : null}
        <Box className="ActionCard-detail">
          <Typography className="ActionCard-detail-label"
                      isLoading={isLoading}
                      variant="body2">Add points</Typography>
          <Typography className="ActionCard-detail-value"
                      isLoading={isLoading}
                      variant="body2">{action?.details?.hasPoints ? 'Yes' : 'No'}</Typography>
        </Box>
        <Box className="ActionCard-detail">
          <Typography className="ActionCard-detail-label"
                      isLoading={isLoading}
                      variant="body2">Multiselect</Typography>
          <Typography className="ActionCard-detail-value"
                      isLoading={isLoading}
                      variant="body2">{action?.details?.multiselect ? 'Yes' : 'No'}</Typography>
        </Box>
        <Box className="ActionCard-detail">
          <Typography className="ActionCard-detail-label"
                      isLoading={isLoading}
                      variant="body2">Autoselect</Typography>
          <Typography className="ActionCard-detail-value"
                      isLoading={isLoading}
                      variant="body2">{autoSelect}</Typography>
        </Box>
        <Box className="ActionCard-detail">
          <Typography className="ActionCard-detail-label"
                      isLoading={isLoading}
                      variant="body2">Tags</Typography>
          <ChipList className="ActionCard-detail-value">
            {action?.details?.tags?.map((t, idx) => {
              let description;
              if (action?.details?.autoTagType === constants.collection.autoTagTypes.byKeywords) {
                if (t.autoTagParams?.keywords?.length > 0) {
                  description = t.autoTagParams.keywords.join(', ');
                }
              } else if ([constants.collection.autoTagTypes.byAI, constants.collection.autoTagTypes.byAIOnline].includes(action?.details?.autoTagType)) {
                if (t.autoTagParams?.description?.length > 0) {
                  description = t.autoTagParams.description;
                }
              }
              if (action?.details?.hasPoints) {
                description = `${description ? `${description} (` : ''}${t.points ?? 0} points${description ? `)` : ''}`;
              }

              return <Chip key={idx}
                           size="small"
                           label={t.value}
                           tooltip={description}
                           TooltipProps={description ? {enterDelay: 0} : {}}
                           variant="outlined"
                           isLoading={isLoading}
                           icon={<Icon icon={Dot} size="smaller" color={action?.details?.color || 'tagGroupDefault.dark'}/>}
                           color={action?.details?.color || 'tagGroupDefault.dark'}/>
            })}
          </ChipList>
        </Box>
      </Box>
    }
  }

  const renderTitle = () => {
    if ([
      constants.assistant.adviceActionTypes.enableService,
      constants.assistant.adviceActionTypes.disableService
    ].includes(action?.id)) {
      // see also assistant
      const typeDef = constants.data.lookup('services', action?.details?.type);
      return constants.assistant.adviceActionTypes.enableService === action?.id ?
        `Suggested service: enable '${typeDef.label}` : `Suggested service: disable '${typeDef.label}`
    } else {
      return action?.title ?? actionType?.label ?? 'Action';
    }
  }

  const renderDescription = () => {
    if ([constants.assistant.adviceActionTypes.askQuestion, constants.assistant.adviceActionTypes.needMoreInfo].includes(action?.id)) {
      return '';
    } else {
      return `${action?.description ? action?.description : ''}${action?.reason ? ` ${action?.reason}` : ''}`;
    }
  }

  innerProps.className = utils.flattenClassName(innerProps.className, {
    type: actionType?.value, clampText: clampText
  });

  return <StyledActionCard ref={innerRef} {...innerProps}>
    <Box className="ActionCard-content">
      {renderLogo()}
      <Box className="ActionCard-info">
        <H6 className="ActionCard-title" showTooltip={true}
            min={8} max={20}
            isLoading={isLoading}>{renderTitle()}</H6>
        <Box className="ActionCard-description-wrapper">
          <Box className="ActionCard-description">
            <Typography className="text" isLoading={isLoading} variant="body2">
              <Markdown disabled={clampText}>{renderDescription()}</Markdown>
            </Typography>
          </Box>
          {clampActive ? <Link className="readMore"
                               isLoading={isLoading}
                               onMouseDown={(e) => e.stopPropagation()}
                               onClick={(e) => {
                                 setClampText((current) => !current);
                                 e.stopPropagation();
                                 e.preventDefault();
                               }}>
            Show {clampText ? 'more' : 'less'}
          </Link> : null}
        </Box>
        {renderDetails()}
      </Box>
    </Box>
  </StyledActionCard>
});

ActionCard.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  action: PropTypes.object,
  answers: PropTypes.array,
  pageIdx: PropTypes.number,
  pageCount: PropTypes.number,
  isLoading: PropTypes.bool,
  clampDescription: PropTypes.bool,
  onChange: PropTypes.func
};

export default ActionCard;
